import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { getChatHistory, saveChatHistory, getUserProfile } from '../../utils/storage';
import { hasCredits, CREDIT_COSTS, syncCreditsFromResponse } from '../../utils/credits';
import { isNativeApp } from '../../utils/platform';
import { aiStream } from '../../services/aiProxy';
import { compressImageFile } from '../../utils/imageUtils';
import PhotoPickerModal from '../PhotoPickerModal';

/* ───────────────────── Constants ───────────────────── */

const MAX_PHOTOS_PER_MSG = 5;
const MAX_PHOTO_SIZE = 20 * 1024 * 1024; // 20 MB

const QUICK_PROMPTS = [
  'I hear a grinding noise when braking',
  'My check engine light just came on',
  'The car pulls to one side while driving',
];

// Mode-specific quick prompts. Shown on the chat empty state and clickable
// to auto-fill the input. Curated so they map directly onto the kind of
// follow-up question the user is most likely to ask after seeing the
// corresponding report.
const ADVISOR_PURCHASE_PROMPTS = [
  "What's the strongest negotiation angle here?",
  'What red flags should I watch for at the test drive?',
  'What questions should I ask the seller?',
  'What must-haves should be on the PPI checklist?',
];

const ADVISOR_REPAIR_PROMPTS = [
  'Is this quote fair for my area?',
  'Are any of these line items unnecessary upsells?',
  'Could I DIY any of this safely?',
  'What questions should I push back with at the shop?',
];

/* ───────────────────── System prompt builder ───────────────────── */

function formatServiceLog(entries) {
  if (!entries || entries.length === 0) return '';
  const lines = entries.map((e) => {
    let line = `- ${e.date}`;
    if (e.mileage) line += ` @ ${Number(e.mileage).toLocaleString()} miles`;
    line += `: ${e.service}`;
    if (e.type) line += ` (${e.type}`;
    if (e.cost != null) line += `, $${Number(e.cost).toLocaleString()}`;
    if (e.type) line += ')';
    if (e.notes) line += ` — ${e.notes}`;
    return line;
  });
  return `\n\nCompleted Service History (these have already been done — do NOT recommend them again):\n${lines.join('\n')}`;
}

function buildSystemPrompt(vehicle, analysis, serviceHistory, hasPhotos) {
  const serviceLogText = formatServiceLog(serviceHistory);
  const profile = getUserProfile();

  const profileSection = profile
    ? `\nOwner Location (ZIP): ${profile.zipCode || 'Unknown'}
Owner Mechanical Level: ${profile.mechanicalLevel || vehicle.skillLevel}${profile.salaryRange && profile.salaryRange !== 'Prefer not to say' ? `\nOwner Income Range: ${profile.salaryRange}` : ''}`
    : '';

  let base = `You are Pocket Mechanic, an AI automotive diagnostic assistant. You are helping the owner of the following vehicle:

Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}
Mileage: ${vehicle.mileage}
Last Service: ${vehicle.lastService || 'Unknown'}
Owner Skill Level: ${vehicle.skillLevel}
Primary Use: ${vehicle.primaryUse}
${vehicle.condition ? `Condition: ${vehicle.condition}` : ''}${profileSection}

Current Health Score: ${analysis?.healthScore || 'N/A'}/100
Health Summary: ${analysis?.healthSummary || 'N/A'}

Known Maintenance Items:
${(analysis?.maintenanceItems || []).map((item) => `- ${item.service} (urgency: ${item.urgency}, difficulty: ${item.difficulty})`).join('\n')}

Market Value: ${analysis?.marketValue?.estimate || 'N/A'} (${analysis?.marketValue?.trend || 'N/A'})
${serviceLogText}

Instructions:
- Diagnose problems based on symptoms described by the user
- Reference the specific vehicle make/model in your answers
- Provide cost estimates broken down by dealer, independent shop, and DIY when relevant
- Tailor technical language to the owner's skill level (${vehicle.skillLevel})
- Be concise but thorough. Use bullet points for clarity.
- If you need more information to diagnose, ask specific follow-up questions.
- Reference the service history when relevant — the owner has already completed those items.`;

  if (hasPhotos) {
    base += `

The user has uploaded photos. Analyze them in the context of their ${vehicle.year} ${vehicle.make} ${vehicle.model}. Look for: damage, wear, incorrect parts, diagnostic clues (dash lights, fluid leaks, tire wear patterns, engine bay modifications, body damage, rust, paint condition, etc.). Be specific about what you see and what it might indicate. If you can identify parts, provide part numbers or names when possible.`;
  }

  return base;
}

/* ───────────────────── Advisor system prompt builders ─────────────────────
 * These mirror buildSystemPrompt but for the Auto Advisor follow-up chats.
 * They take the same `data` shape AutoAdvisor uses internally, so we can
 * pass it straight through without translating fields. */

function buildAdvisorPurchaseSystemPrompt(advisorData, hasPhotos) {
  const d = advisorData || {};
  const r = d.results || {};
  const vehicleLabel =
    r.vehicleLabel ||
    [d.year, d.make, d.model, d.trim].filter(Boolean).join(' ') ||
    'this vehicle';

  // The user may have looked at a specific listing OR generally researched a
  // model. We tell the assistant which mode they were in so it can tailor
  // advice (negotiation tactics vs. shopping criteria).
  const specificity = d.hasSpecificListing
    ? `They were evaluating a SPECIFIC listing${d.listingUrl ? ` (${d.listingUrl})` : ''}${d.askingPrice ? ` priced at ${d.askingPrice}` : ''}.`
    : 'They were researching this model in general — no specific listing yet.';

  const intent =
    d.intent === 'curious' ? 'Just curious / learning'
      : d.intent === 'passive' ? 'Passively monitoring the market'
        : d.intent === 'active' ? 'Actively shopping / comparing examples'
          : d.intent === 'ready' ? 'Ready to buy — about to make an offer'
            : 'Unknown intent';

  // Compact the report findings the user already saw, so the assistant can
  // reference them by name ("you saw the Cars & Bids comp at $29,200…")
  // rather than re-deriving them from scratch.
  const reportSummary = (r.sections || [])
    .map((s) => `- ${s.title}: ${summariseSection(s)}`)
    .join('\n') || '(report not yet generated)';

  let base = `You are Proper Miles' Pre-Purchase Advisor — the same advisor that just delivered a written report. The user has tapped "Ask a question" to follow up.

Vehicle: ${vehicleLabel}
Buyer phase: ${intent}
${specificity}
${d.intendedUse ? `Intended use: ${d.intendedUse}` : ''}
${d.budgetMin || d.budgetMax ? `Budget: ${d.budgetMin || '?'} – ${d.budgetMax || '?'}` : ''}
${d.mileage ? `Mileage on the listing: ${d.mileage}` : ''}
${d.condition ? `Condition described: ${d.condition}` : ''}

Report sections the user already saw:
${reportSummary}

Instructions:
- Stay in advisor voice — confident, honest, concise. Avoid disclaimers.
- Reference the report directly when relevant ("as your valuation showed…")
- For negotiation, give specific scripts, not generic advice.
- For red flags / inspection items, name the part, not just the symptom.
- If the user asks something the report didn't cover, just answer it cleanly.
- Use markdown sparingly: short bullets, bold for the takeaway.`;

  if (hasPhotos) {
    base += `\n\nThe user may attach photos of the car, the listing, the VIN tag, or a competing example. Analyze them in the context of buying this vehicle.`;
  }
  return base;
}

function buildAdvisorRepairSystemPrompt(advisorData, hasPhotos) {
  const d = advisorData || {};
  const r = d.results || {};
  const vehicleLabel =
    r.vehicleLabel ||
    [d.repairVehicleYear, d.repairVehicleMake, d.repairVehicleModel].filter(Boolean).join(' ') ||
    'their vehicle';

  // Render the line items the user submitted so the assistant can argue
  // specific items rather than the whole quote in aggregate.
  const lineItems = (d.repairLineItems || [])
    .filter((it) => it && (it.description || it.cost))
    .map((it) => `- ${it.description || '(no description)'}${it.cost ? ` — $${it.cost}` : ''}`)
    .join('\n');

  const reportSummary = (r.sections || [])
    .map((s) => `- ${s.title}: ${summariseSection(s)}`)
    .join('\n') || '(report not yet generated)';

  let base = `You are Proper Miles' Repair Quote Advisor — the same advisor that just analyzed the user's quote. The user has tapped "Ask a question" to follow up.

Vehicle: ${vehicleLabel}${d.repairMileage ? ` @ ${d.repairMileage} miles` : ''}
Location (ZIP): ${d.repairZipCode || 'Unknown'}
Shop: ${d.repairShopName || 'Unnamed shop'}
Total quoted: ${d.repairTotalQuoted ? '$' + d.repairTotalQuoted : 'Not stated'}
Repair description: ${d.repairDescription || '(see photo)'}

Line items submitted:
${lineItems || '(none — quote was submitted as a photo or single total)'}

Report sections the user already saw:
${reportSummary}

Instructions:
- Stay in advisor voice — direct, plainspoken, no hedging.
- Reference the report ("as the price comparison showed…") when relevant.
- For "is this fair?" questions, give a $ range and your verdict in one line.
- For pushback / negotiation, give a specific script the user can read aloud.
- Call out individual line items by name when they're high or low.
- For DIY questions, give honest difficulty + tools required + time estimate.
- Use markdown sparingly: short bullets, bold for the takeaway.`;

  if (hasPhotos) {
    base += `\n\nThe user may attach photos of the quote, the damaged part, or another shop's estimate. Use them to spot inconsistencies, missing labor codes, or upsell items.`;
  }
  return base;
}

/* Compact a report section into one-line context for the system prompt.
 * We don't dump the entire HTML/JSON — just enough so the assistant knows
 * what the user looked at and can refer back to it. */
function summariseSection(section) {
  const c = section?.content || {};
  if (c.range) return `range ${c.range}${c.verdict ? ' — ' + c.verdict.slice(0, 120) : ''}`;
  if (c.verdict) return c.verdict.slice(0, 160);
  if (c.summary) return c.summary.slice(0, 160);
  if (c.dailyScore != null && c.weekendScore != null) return `daily ${c.dailyScore}/10, weekend ${c.weekendScore}/10`;
  if (Array.isArray(c.items)) return `${c.items.length} checklist items`;
  if (Array.isArray(c.perspectives)) return `${c.perspectives.length} perspectives`;
  return '(see report)';
}

/* ───────────────────── Message content builder for API ───────────────────── */

function buildApiMessages(messages, vehicle, analysis, serviceHistory, mode, advisorData) {
  // Check if any message in the conversation has photos
  const conversationHasPhotos = messages.some((m) => m.photos && m.photos.length > 0);

  let systemContent;
  if (mode === 'advisor-purchase') {
    systemContent = buildAdvisorPurchaseSystemPrompt(advisorData, conversationHasPhotos);
  } else if (mode === 'advisor-repair') {
    systemContent = buildAdvisorRepairSystemPrompt(advisorData, conversationHasPhotos);
  } else {
    systemContent = buildSystemPrompt(vehicle, analysis, serviceHistory, conversationHasPhotos);
  }

  const apiMessages = [
    { role: 'system', content: systemContent },
  ];

  for (const msg of messages) {
    if (msg.role === 'user' && msg.photos && msg.photos.length > 0) {
      // Build multi-modal content array for user messages with photos
      // Filter out photos with missing dataUrl (stripped by localStorage quota recovery)
      const validPhotos = msg.photos.filter((p) => p.dataUrl && p.dataUrl.length > 0);
      if (validPhotos.length > 0) {
        const content = [];
        if (msg.content) {
          content.push({ type: 'text', text: msg.content });
        }
        for (const photo of validPhotos) {
          content.push({
            type: 'image_url',
            image_url: { url: photo.dataUrl, detail: 'high' },
          });
        }
        apiMessages.push({ role: 'user', content });
      } else {
        // Photos were stripped — send as text-only message
        apiMessages.push({ role: 'user', content: msg.content || '[Photo no longer available]' });
      }
    } else {
      apiMessages.push({ role: msg.role, content: msg.content });
    }
  }

  return apiMessages;
}

/* ───────────────────── Photo lightbox component ───────────────────── */

function Lightbox({ src, alt, onClose }) {
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        cursor: 'zoom-out',
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          borderRadius: 8,
        }}
      />
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)',
          color: 'white',
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        &times;
      </button>
    </div>
  );
}

/* ───────────────────── Photo thumbnails in message bubble ───────────────────── */

function MessagePhotos({ photos, onPhotoClick }) {
  if (!photos || photos.length === 0) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: photos.length === 1 ? '1fr' : 'repeat(2, 1fr)',
        gap: 6,
        marginBottom: 8,
      }}
    >
      {photos.map((photo, i) => (
        <div
          key={photo.id || i}
          onClick={() => onPhotoClick(photo)}
          style={{
            position: 'relative',
            aspectRatio: photos.length === 1 ? '16/10' : '1',
            borderRadius: 8,
            overflow: 'hidden',
            cursor: 'zoom-in',
          }}
        >
          <img
            src={photo.dataUrl}
            alt={photo.label || `Photo ${i + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>
      ))}
    </div>
  );
}

/* ───────────────────── Pending photo preview strip ───────────────────── */

function PendingPhotos({ photos, onRemove }) {
  if (photos.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: '8px 20px',
        overflowX: 'auto',
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-primary)',
        flexShrink: 0,
      }}
    >
      {photos.map((photo) => (
        <div
          key={photo.id}
          style={{
            position: 'relative',
            width: 64,
            height: 64,
            borderRadius: 8,
            overflow: 'hidden',
            flexShrink: 0,
            border: '1px solid var(--border)',
          }}
        >
          <img
            src={photo.dataUrl}
            alt={photo.label}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <button
            onClick={() => onRemove(photo.id)}
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      ))}
      <div
        style={{
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
          alignSelf: 'center',
          whiteSpace: 'nowrap',
          paddingRight: 4,
        }}
      >
        {photos.length}/{MAX_PHOTOS_PER_MSG}
      </div>
    </div>
  );
}

/* ───────────────────── Main Chat component ───────────────────── */

export default function Chat({
  // Default 'vehicle' mode — used everywhere except Auto Advisor follow-ups.
  mode = 'vehicle',
  // Vehicle-mode props (required when mode === 'vehicle')
  vehicle, vehicleId, analysis, serviceHistory,
  // Advisor-mode props (required when mode is 'advisor-purchase' | 'advisor-repair')
  advisorData,
  // Common
  initialMessage, onBack, onToast,
}) {
  const isAdvisor = mode === 'advisor-purchase' || mode === 'advisor-repair';

  // Storage key — keep vehicle chats keyed by vehicleId (unchanged), and key
  // advisor chats by their saved report (or a transient 'current' bucket if
  // the user hasn't saved yet). This way reopening a saved report later
  // brings the chat history back too.
  const chatId = isAdvisor
    ? (advisorData?.savedAt
        ? `advisor_${mode === 'advisor-repair' ? 'repair' : 'purchase'}_${advisorData.savedAt}`
        : `advisor_${mode === 'advisor-repair' ? 'repair' : 'purchase'}_current`)
    : vehicleId;

  const [messages, setMessages] = useState(() => getChatHistory(chatId));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const abortRef = useRef(null);

  // Header copy + empty-state copy + quick prompts vary by mode.
  const headerTitle = isAdvisor
    ? (mode === 'advisor-repair' ? 'Repair Quote Advisor' : 'Pre-Purchase Advisor')
    : 'Pocket Mechanic';

  const vehicleLabel = !isAdvisor
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`
    : (advisorData?.results?.vehicleLabel
        || [advisorData?.year, advisorData?.make, advisorData?.model].filter(Boolean).join(' ')
        || [advisorData?.repairVehicleYear, advisorData?.repairVehicleMake, advisorData?.repairVehicleModel].filter(Boolean).join(' ')
        || 'Your report');

  const emptyTitle = isAdvisor
    ? 'Ask a follow-up'
    : "What's going on?";
  const emptyDescription = isAdvisor
    ? (mode === 'advisor-repair'
        ? `I have your full repair-quote analysis loaded. Ask me anything about pricing, line items, DIY options, or how to push back at the shop.`
        : `I have your full pre-purchase report loaded. Ask me anything about negotiation, red flags, inspection items, or how this vehicle compares.`)
    : `Describe any issues, strange noises, or warning lights. I have your ${vehicleLabel}'s full maintenance profile loaded.`;

  const quickPrompts = isAdvisor
    ? (mode === 'advisor-repair' ? ADVISOR_REPAIR_PROMPTS : ADVISOR_PURCHASE_PROMPTS)
    : QUICK_PROMPTS;

  /* Scroll to bottom instantly on mount, smoothly on updates */
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      // Instant scroll on first render (returning to chat)
      hasMounted.current = true;
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
      });
    } else {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, streamingContent]);

  useEffect(() => {
    saveChatHistory(chatId, messages);
  }, [chatId, messages]);

  /* Keep input visible when iOS keyboard opens */
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handleFocus = () => {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    };
    el.addEventListener('focus', handleFocus);
    return () => el.removeEventListener('focus', handleFocus);
  }, []);

  /* ── Photo handling ── */

  function handlePhotoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_PHOTOS_PER_MSG - pendingPhotos.length;
    const toProcess = files.slice(0, remaining);

    toProcess.forEach((file) => {
      if (file.size > MAX_PHOTO_SIZE) {
        if (import.meta.env.DEV) console.warn(`[Chat] Skipping ${file.name}: exceeds 20MB`);
        return;
      }
      compressImageFile(file, 1200, 0.7).then((compressed) => {
        setPendingPhotos((prev) => {
          if (prev.length >= MAX_PHOTOS_PER_MSG) return prev;
          return [
            ...prev,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              dataUrl: compressed,
              label: file.name,
            },
          ];
        });
      }).catch(() => {
        if (import.meta.env.DEV) console.warn(`[Chat] Failed to compress ${file.name}`);
      });
    });

    if (fileRef.current) fileRef.current.value = '';
  }

  function removePendingPhoto(id) {
    setPendingPhotos((prev) => prev.filter((p) => p.id !== id));
  }

  /* ── Send message (core logic) ── */

  async function sendText(text, photos = []) {
    if ((!text && photos.length === 0) || loading) return;

    // UI-only credit check (server enforces the real check)
    const creditCost = photos.length > 0 ? CREDIT_COSTS.chatMessagePhoto : CREDIT_COSTS.chatMessage;
    if (!hasCredits(creditCost)) {
      onToast?.('No credits remaining. Upgrade your plan for more.');
      return;
    }

    const userMsg = {
      role: 'user',
      content: text || (photos.length > 0 ? 'What do you see in these photos?' : ''),
      ...(photos.length > 0 ? { photos } : {}),
    };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setPendingPhotos([]);
    setLoading(true);
    setStreamingContent('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const apiMessages = buildApiMessages(updatedMessages, vehicle, analysis, serviceHistory, mode, advisorData);
      const conversationHasPhotos = updatedMessages.some((m) => m.photos?.length > 0);

      const res = await aiStream({
        creditCost,
        model: conversationHasPhotos ? 'gpt-4o' : 'gpt-4o-mini',
        messages: apiMessages,
        temperature: 0.6,
        max_tokens: 2000,
      }, { signal: controller.signal });

      // Read SSE stream — with multiple fallbacks for webview compatibility
      let fullContent = '';

      if (res.body && typeof res.body.getReader === 'function') {
        // Streaming path: ReadableStream supported
        let reader;
        if (typeof TextDecoderStream !== 'undefined') {
          reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        } else {
          const rawReader = res.body.getReader();
          const decoder = new TextDecoder();
          reader = {
            async read() {
              const { done, value } = await rawReader.read();
              if (done) return { done, value: undefined };
              return { done, value: decoder.decode(value, { stream: true }) };
            },
          };
        }

        let buffer = '';
        let lastDataTime = Date.now();
        const streamTimeout = setInterval(() => {
          if (Date.now() - lastDataTime > 30_000) {
            controller.abort();
            clearInterval(streamTimeout);
          }
        }, 5_000);

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            lastDataTime = Date.now();

            buffer += value;
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;
              try {
                const parsed = JSON.parse(data);
                if (parsed.delta) {
                  fullContent += parsed.delta;
                  setStreamingContent(fullContent);
                }
                if (parsed.credits_remaining != null) {
                  syncCreditsFromResponse(parsed.credits_remaining);
                }
              } catch { /* skip malformed events */ }
            }
          }
        } finally {
          clearInterval(streamTimeout);
        }
      } else {
        // Non-streaming fallback: iPad/older WKWebView where res.body is null
        const rawText = await res.text();
        const lines = rawText.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) fullContent += parsed.delta;
            if (parsed.credits_remaining != null) {
              syncCreditsFromResponse(parsed.credits_remaining);
            }
          } catch { /* skip malformed events */ }
        }
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: fullContent }]);
      setStreamingContent('');
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (import.meta.env.DEV) console.error('Chat error:', err);

      let errorContent;
      if (err.code === 'NO_CREDITS') {
        errorContent = 'You\'ve run out of credits. Upgrade your plan for more.';
      } else if (err.code === 'RATE_LIMITED') {
        errorContent = 'Too many requests. Please wait a moment and try again.';
      } else if (err.message?.includes('NOT_AUTHENTICATED') || err.code === 'UNAUTHORIZED') {
        errorContent = 'Your session has expired. Please sign out and sign back in.';
      } else if (err.code === 'AI_UNAVAILABLE') {
        errorContent = 'The AI service is temporarily unavailable. Please try again later.';
      } else if (err.code === 'TIMEOUT') {
        errorContent = 'The request timed out. Please try a shorter message or try again.';
      } else if (err.code === 'UPSTREAM_ERROR' || err.code === 'CREDIT_ERROR') {
        errorContent = 'Something went wrong on our end. Please try again in a moment.';
      } else {
        errorContent = 'Sorry, I wasn\'t able to process that request. Please check your internet connection and try again.';
        if (import.meta.env.DEV) errorContent += ` (${err.code || err.message || 'unknown'})`;
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: errorContent }]);
      setStreamingContent('');
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage(e) {
    e.preventDefault();
    await sendText(input.trim(), [...pendingPhotos]);
  }

  /* ── Auto-send initial message (from DIY guide "Ask Pocket Mechanic") ── */
  const initialMessageSent = useRef(false);
  useEffect(() => {
    if (initialMessage && !initialMessageSent.current) {
      initialMessageSent.current = true;
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        sendText(initialMessage);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canSend = input.trim() || pendingPhotos.length > 0;
  const hasPhotosOrText = canSend;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      background: 'var(--bg-primary, #1a1a1a)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Lightbox */}
      {lightboxPhoto && (
        <Lightbox
          src={lightboxPhoto.dataUrl}
          alt={lightboxPhoto.label}
          onClose={() => setLightboxPhoto(null)}
        />
      )}

      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => {
            abortRef.current?.abort();
            onBack();
          }}
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--bg-card)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{headerTitle}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{vehicleLabel}</div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {messages.length === 0 && !streamingContent && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--accent-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--accent)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
              </svg>
            </div>
            <h3 style={{ fontWeight: 600, marginBottom: 6 }}>{emptyTitle}</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.5 }}>
              {emptyDescription}
            </p>
            {!isAdvisor && (
              <p
                style={{
                  color: 'var(--text-muted)',
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                  marginTop: 8,
                }}
              >
                Tap the camera icon to upload photos for visual diagnosis.
              </p>
            )}

            {/* Quick prompts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.875rem', padding: '10px 16px' }}
                  onClick={() => {
                    setInput(prompt);
                    inputRef.current?.focus();
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '12px 16px',
                borderRadius:
                  msg.role === 'user'
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-card)',
                border:
                  msg.role === 'user' ? 'none' : '1px solid var(--border)',
                fontSize: '0.9375rem',
                lineHeight: 1.5,
                whiteSpace: msg.role === 'user' ? 'pre-wrap' : undefined,
              }}
              className={msg.role === 'assistant' ? 'chat-markdown' : undefined}
            >
              {/* Photos in user messages */}
              {msg.role === 'user' && msg.photos && (
                <MessagePhotos
                  photos={msg.photos}
                  onPhotoClick={setLightboxPhoto}
                />
              )}

              {msg.role === 'assistant' ? (
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}

        {/* Streaming response */}
        {streamingContent && (
          <div style={{ display: 'flex', marginBottom: 12 }}>
            <div
              className="chat-markdown"
              style={{
                maxWidth: '85%',
                padding: '12px 16px',
                borderRadius: '16px 16px 16px 4px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                fontSize: '0.9375rem',
                lineHeight: 1.5,
              }}
            >
              <ReactMarkdown>{streamingContent}</ReactMarkdown>
              <span style={{ animation: 'pulse 1s infinite', opacity: 0.6 }}>▍</span>
            </div>
          </div>
        )}

        {/* Typing indicator (with photo-aware label) */}
        {loading && !streamingContent && (
          <div style={{ display: 'flex', marginBottom: 12 }}>
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '16px 16px 16px 4px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
              }}
            >
              {/* Check if the last user message had photos */}
              {messages.length > 0 &&
                messages[messages.length - 1]?.photos?.length > 0 && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      marginBottom: 6,
                    }}
                  >
                    Analyzing photos...
                  </div>
                )}
              <div style={{ display: 'flex', gap: 4 }}>
                <span style={dotStyle(0)} />
                <span style={dotStyle(1)} />
                <span style={dotStyle(2)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pending photo preview strip */}
      <PendingPhotos photos={pendingPhotos} onRemove={removePendingPhoto} />

      {/* Input area */}
      <form
        onSubmit={sendMessage}
        style={{
          padding: '12px 20px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {/* Hidden file input */}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handlePhotoUpload}
          style={{ display: 'none' }}
        />

        {/* Camera/photo button */}
        <button
          type="button"
          onClick={() => setShowPhotoPicker(true)}
          disabled={loading || pendingPhotos.length >= MAX_PHOTOS_PER_MSG}
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.2s',
            opacity: loading || pendingPhotos.length >= MAX_PHOTOS_PER_MSG ? 0.4 : 1,
          }}
          title="Upload photos"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--text-secondary)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        </button>

        {/* Text input */}
        <input
          ref={inputRef}
          className="form-input"
          placeholder={pendingPhotos.length > 0 ? 'Add context about the photos...' : 'Describe the issue...'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          style={{ flex: 1 }}
        />

        {/* Send button */}
        <button
          type="submit"
          disabled={!hasPhotosOrText || loading}
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-sm)',
            background: hasPhotosOrText && !loading ? 'var(--accent)' : 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.2s',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={hasPhotosOrText && !loading ? 'white' : 'var(--text-muted)'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          fileRef.current.removeAttribute('capture');
          fileRef.current.click();
        }}
      />

      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .chat-markdown p { margin: 0 0 0.5em; }
        .chat-markdown p:last-child { margin-bottom: 0; }
        .chat-markdown strong { color: var(--text-primary); font-weight: 600; }
        .chat-markdown em { color: var(--text-secondary); }
        .chat-markdown a { color: var(--accent); text-decoration: underline; }
        .chat-markdown ul, .chat-markdown ol {
          margin: 0.4em 0;
          padding-left: 1.4em;
        }
        .chat-markdown li { margin-bottom: 0.25em; }
        .chat-markdown li:last-child { margin-bottom: 0; }
        .chat-markdown code {
          background: var(--bg-elevated);
          padding: 0.15em 0.4em;
          border-radius: 4px;
          font-size: 0.85em;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .chat-markdown pre {
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 12px;
          overflow-x: auto;
          margin: 0.5em 0;
        }
        .chat-markdown pre code {
          background: none;
          padding: 0;
          font-size: 0.85em;
        }
        .chat-markdown h1, .chat-markdown h2, .chat-markdown h3,
        .chat-markdown h4, .chat-markdown h5, .chat-markdown h6 {
          margin: 0.6em 0 0.3em;
          font-weight: 600;
          line-height: 1.3;
        }
        .chat-markdown h1 { font-size: 1.1em; }
        .chat-markdown h2 { font-size: 1.05em; }
        .chat-markdown h3 { font-size: 1em; }
        .chat-markdown blockquote {
          border-left: 3px solid var(--accent);
          margin: 0.5em 0;
          padding: 0.25em 0 0.25em 12px;
          color: var(--text-secondary);
        }
        .chat-markdown hr {
          border: none;
          border-top: 1px solid var(--border);
          margin: 0.75em 0;
        }
        .chat-markdown table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.5em 0;
          font-size: 0.85em;
        }
        .chat-markdown th, .chat-markdown td {
          border: 1px solid var(--border);
          padding: 6px 10px;
          text-align: left;
        }
        .chat-markdown th {
          background: var(--bg-elevated);
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

function dotStyle(delay) {
  return {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--text-muted)',
    animation: `typingBounce 1.2s ${delay * 0.15}s infinite`,
  };
}
